import React, { useCallback, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { SuiNotificationGroup, SuiSpinner } from '@splashthat/sui';

import { getAgendaTouchpointApiClient } from 'lib/SplashApiClient/subclients/AgendaTouchpointApiClient';
import { Node, useEditor } from '@splashthat/splash-cms-engine';
import { SmoothScrollBehavior } from 'common/components/TouchpointEditor/components/TouchpointContainer';
import Headline from 'common/components/TouchpointEditor/components/TouchpointNodes/Headline';
import Paragraph from 'common/components/TouchpointEditor/components/TouchpointNodes/Paragraph';
import SpeakerModal from 'common/components/TouchpointEditor/components/TouchpointNodes/SpeakerModal';
import Logo from 'common/components/TouchpointEditor/components/TouchpointNodes/Logo';
import { useCustomCss } from 'common/components/TouchpointEditor/contexts/CustomCssContext';
import { cssValidator } from 'common/components/CodeEditor/validate';

import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { usePreview } from 'common/components/TouchpointEditor/hooks/usePreview';
import { useGuestAccessHash } from 'utils/hooks/useGuestAccessHash';
import {
    useSessionRegistrationButtonsStateMachine,
    PRE_EVENT_REGISTRATION,
} from 'common/compounds/AgendaTouchpoint/contexts/SessionRegistrationStateProvider';
import GuestPageFooter from 'common/compounds/GuestPageFooter/GuestPageFooter';
import Logger from 'observability/Logger';
import { getAgendaPageConfig } from '../lib/agendaPageConfig';
import Header from './nodes/Header';
import {
    NODE_DISPLAY_NAME_ADD_SESSION_TO_CALENDAR_MODAL,
    NODE_DISPLAY_NAME_BACK_BUTTON,
    NODE_DISPLAY_NAME_CALL_TO_ACTION,
    NODE_DISPLAY_NAME_CALL_TO_ACTION_BUTTON,
    NODE_DISPLAY_NAME_CALL_TO_ACTION_HEADLINE,
    NODE_DISPLAY_NAME_CALL_TO_ACTION_PARAGRAPH,
    NODE_DISPLAY_NAME_COVER,
    NODE_DISPLAY_NAME_DATE,
    NODE_DISPLAY_NAME_EVENT_TITLE,
    NODE_DISPLAY_NAME_HEADER,
    NODE_DISPLAY_NAME_LOGO,
    NODE_DISPLAY_NAME_PARAGRAPH,
    NODE_DISPLAY_NAME_REGISTER_BUTTON,
    NODE_DISPLAY_NAME_SESSION_CARDS,
    NODE_DISPLAY_NAME_SESSION_FILTER,
    NODE_DISPLAY_NAME_SESSION_LIST,
    NODE_DISPLAY_NAME_SESSION_TAB,
    NODE_DISPLAY_NAME_SESSION_NAVIGATION,
    NODE_DISPLAY_NAME_SESSION_SEARCH,
    NODE_DISPLAY_NAME_SPEAKER_MODALS,
    NODE_DISPLAY_NAME_VIEW_AGENDA,
    NODE_DISPLAY_NAME_SESSIONS_CALENDAR_DOWNLOAD,
    NODE_ID_ADD_SESSION_TO_CALENDAR,
    NODE_ID_BACK_BUTTON,
    NODE_ID_CALL_TO_ACTION,
    NODE_ID_CALL_TO_ACTION_BUTTON,
    NODE_ID_CALL_TO_ACTION_HEADLINE,
    NODE_ID_CALL_TO_ACTION_PARAGRAPH,
    NODE_ID_COVER,
    NODE_ID_DATE,
    NODE_ID_EVENT_TITLE,
    NODE_ID_HEADER,
    NODE_ID_LOGO,
    NODE_ID_PARAGRAPH,
    NODE_ID_REGISTER_BUTTON,
    NODE_ID_SESSION_CARDS,
    NODE_ID_SESSION_FILTER,
    NODE_ID_SESSION_LIST,
    NODE_ID_SESSION_TAB,
    NODE_ID_SESSION_NAVIGATION,
    NODE_ID_SESSION_SEARCH,
    NODE_ID_SPEAKER_MODALS,
    NODE_ID_VIEW_AGENDA,
    NODE_ID_SESSIONS_CALENDAR_DOWNLOAD,
} from './constants';
import BackButtonLink from './nodes/Link/SubTypes/BackButtonLink';
import ViewAgendaLink from './nodes/Link/SubTypes/ViewAgendaLink';
import RegisterButton from './nodes/Button/SubTypes/RegisterButton';
import Block, { SessionsListBlock, SessionsNavigationBlock } from './nodes/Block';
import {
    AgendaGuestExperienceContainer,
    BlockContentContainer,
    CoverLinksContainer,
    SessionsNavigationBlockContentContainer,
    LoadingSpinnerContainer,
    StyledTabsRoot,
    StyledTabsContent,
} from './AgendaTouchpoint.styles';
import Date from './nodes/Date';
import CallToActionParagraph from './nodes/Paragraph/SubTypes/CallToActionParagraph';
import SessionSearch from './nodes/SessionSearch';
import SessionTab from './nodes/SessionTab';
import SessionFilter from './nodes/SessionFilter';
import SessionsList from './nodes/SessionsList';
import AttendeeSessionsList from './nodes/SessionsList/AttendeeSessionsList';
import EventTitleHeadline from './nodes/Headline/SubTypes/EventTitleHeadline';
import { AddToCalendarModal } from './nodes/AddToCalendarModal/AddToCalendarModal';
import { AddToCalendarButton } from './nodes/Button/SubTypes/AddToCalendarButton';

const LOG = new Logger('AgendaContent');
const STYLES_RETRIEVAL_ERROR_MESSAGE = 'We could not get the stored styles of the Agenda page, showing default styles.';

const TABS = {
    ALL_SESSIONS: 'allSessions',
    MY_SESSIONS: 'mySessions',
};

const AgendaContent = ({ hideFooter }) => {
    const editor = useEditor(({ editing, events: { selected } }) => ({ editing, selected }));
    const { event } = useTouchpointData();
    const { shouldPreview } = usePreview({ event });
    const isEditing = editor.editing;
    const { guestAccessHash } = useGuestAccessHash(`sessionsRegistration/${event?.id}/guestAccessHash`);
    const agendaApiClient = useRef(getAgendaTouchpointApiClient(event.id, isEditing)).current;
    const { isLoading, isError, data } = useQuery(['agendaPage', event.id], agendaApiClient.get, {
        staleTime: Infinity,
        cacheTime: 0,
    });
    const getConfig = useCallback(() => getAgendaPageConfig(data), [data]);
    const [notificationDismissed, setNotificationDismissed] = useState(false);
    const { customCss } = useCustomCss();

    const { agendaPreviewState } = useSessionRegistrationButtonsStateMachine();

    useEffect(() => {
        if (!customCss) {
            return;
        }

        const styleEl = document.createElement('style');
        styleEl.textContent = customCss;
        const cssErrors = cssValidator(customCss);
        if (cssErrors.length > 0) {
            LOG.error('CSS Validation Errors:', cssErrors);
            return;
        }
        document.head.appendChild(styleEl);
    }, [customCss]);

    if (isLoading) {
        return (
            // The aria-label below does nothing for now, expecting to give support from SUI
            <LoadingSpinnerContainer>
                <SuiSpinner loading size="large" aria-label="loading" />
            </LoadingSpinnerContainer>
        );
    }
    const activeNotifications = isError
        ? [
              {
                  message: STYLES_RETRIEVAL_ERROR_MESSAGE,
                  variation: 'ERROR',
                  onClose: () => setNotificationDismissed(true),
              },
          ]
        : [];

    const agendaPageConfig = getConfig();

    const apiSessionCardsMeta = agendaPageConfig.sessionCards?.meta;
    const normalizedSessionCardsMeta = Array.isArray(apiSessionCardsMeta) ? {} : apiSessionCardsMeta;

    const sessionCardsMeta = {
        ...AttendeeSessionsList.splash.defaultProps.meta,
        ...(normalizedSessionCardsMeta || {}),
    };

    let showRegisterCTA = isEditing || guestAccessHash === null;

    if (shouldPreview) {
        showRegisterCTA = agendaPreviewState === PRE_EVENT_REGISTRATION;
    }

    return (
        <AgendaGuestExperienceContainer $editing={isEditing} $previewing={shouldPreview}>
            {!isEditing && <SmoothScrollBehavior />}
            <Node is={Header} displayName={NODE_DISPLAY_NAME_HEADER} id={NODE_ID_HEADER} {...agendaPageConfig.header}>
                <Node
                    is={BackButtonLink}
                    displayName={NODE_DISPLAY_NAME_BACK_BUTTON}
                    id={NODE_ID_BACK_BUTTON}
                    {...agendaPageConfig.backButton}
                    dataQa="Back-Button-Link"
                />
                <Node is={Logo} displayName={NODE_DISPLAY_NAME_LOGO} id={NODE_ID_LOGO} {...agendaPageConfig.logo} />
            </Node>
            <Node is={Block} displayName={NODE_DISPLAY_NAME_COVER} id={NODE_ID_COVER} {...agendaPageConfig.cover}>
                <BlockContentContainer>
                    <Node is={Date} displayName={NODE_DISPLAY_NAME_DATE} id={NODE_ID_DATE} {...agendaPageConfig.date} />
                    <Node
                        is={EventTitleHeadline}
                        displayName={NODE_DISPLAY_NAME_EVENT_TITLE}
                        id={NODE_ID_EVENT_TITLE}
                        {...agendaPageConfig.eventTitle}
                    />
                    <Node
                        is={Paragraph}
                        displayName={NODE_DISPLAY_NAME_PARAGRAPH}
                        id={NODE_ID_PARAGRAPH}
                        {...agendaPageConfig.paragraph}
                    />
                    <CoverLinksContainer>
                        {showRegisterCTA && (
                            <Node
                                is={RegisterButton}
                                displayName={NODE_DISPLAY_NAME_REGISTER_BUTTON}
                                id={NODE_ID_REGISTER_BUTTON}
                                {...agendaPageConfig.registerButton}
                            />
                        )}
                        <Node
                            is={ViewAgendaLink}
                            displayName={NODE_DISPLAY_NAME_VIEW_AGENDA}
                            id={NODE_ID_VIEW_AGENDA}
                            {...agendaPageConfig.viewAgenda}
                        />
                    </CoverLinksContainer>
                </BlockContentContainer>
            </Node>
            <StyledTabsRoot defaultValue={TABS.ALL_SESSIONS}>
                <Node
                    is={SessionsNavigationBlock}
                    displayName={NODE_DISPLAY_NAME_SESSION_NAVIGATION}
                    id={NODE_ID_SESSION_NAVIGATION}
                    {...agendaPageConfig.sessionNavigation}
                >
                    <SessionsNavigationBlockContentContainer>
                        <Node
                            is={SessionSearch}
                            displayName={NODE_DISPLAY_NAME_SESSION_SEARCH}
                            id={NODE_ID_SESSION_SEARCH}
                            {...agendaPageConfig.sessionSearch}
                        />
                        <Node
                            is={SessionFilter}
                            displayName={NODE_DISPLAY_NAME_SESSION_FILTER}
                            id={NODE_ID_SESSION_FILTER}
                            {...agendaPageConfig.sessionFilter}
                        />
                        <Node
                            is={SessionTab}
                            displayName={NODE_DISPLAY_NAME_SESSION_TAB}
                            id={NODE_ID_SESSION_TAB}
                            {...agendaPageConfig.sessionTab}
                        />
                        <Node
                            is={AddToCalendarButton}
                            displayName={NODE_DISPLAY_NAME_SESSIONS_CALENDAR_DOWNLOAD}
                            id={NODE_ID_SESSIONS_CALENDAR_DOWNLOAD}
                            {...agendaPageConfig.addSessionsToCalendarButton}
                            guestAccessHash={guestAccessHash}
                            eventId={event.id}
                        />
                    </SessionsNavigationBlockContentContainer>
                </Node>

                <Node
                    is={SessionsListBlock}
                    displayName={NODE_DISPLAY_NAME_SESSION_LIST}
                    id={NODE_ID_SESSION_LIST}
                    {...agendaPageConfig.sessionList}
                >
                    <StyledTabsContent value={TABS.ALL_SESSIONS}>
                        <Node
                            is={SessionsList}
                            displayName={NODE_DISPLAY_NAME_SESSION_CARDS}
                            id={NODE_ID_SESSION_CARDS}
                            {...agendaPageConfig.sessionCards}
                            meta={sessionCardsMeta}
                        />
                    </StyledTabsContent>
                    <StyledTabsContent value={TABS.MY_SESSIONS}>
                        <Node
                            is={AttendeeSessionsList}
                            displayName={NODE_DISPLAY_NAME_SESSION_CARDS}
                            id={NODE_ID_SESSION_CARDS}
                            {...agendaPageConfig.sessionCards}
                            meta={sessionCardsMeta}
                        />
                    </StyledTabsContent>
                </Node>
            </StyledTabsRoot>
            {showRegisterCTA && (
                <Node
                    is={Block}
                    displayName={NODE_DISPLAY_NAME_CALL_TO_ACTION}
                    id={NODE_ID_CALL_TO_ACTION}
                    {...agendaPageConfig.callToAction}
                >
                    <BlockContentContainer>
                        <Node
                            is={Headline}
                            displayName={NODE_DISPLAY_NAME_CALL_TO_ACTION_HEADLINE}
                            id={NODE_ID_CALL_TO_ACTION_HEADLINE}
                            {...agendaPageConfig.callToActionHeadline}
                        />
                        <Node
                            is={CallToActionParagraph}
                            displayName={NODE_DISPLAY_NAME_CALL_TO_ACTION_PARAGRAPH}
                            id={NODE_ID_CALL_TO_ACTION_PARAGRAPH}
                            {...agendaPageConfig.callToActionParagraph}
                        />
                        <Node
                            is={RegisterButton}
                            displayName={NODE_DISPLAY_NAME_CALL_TO_ACTION_BUTTON}
                            id={NODE_ID_CALL_TO_ACTION_BUTTON}
                            {...agendaPageConfig.callToActionButton}
                        />
                    </BlockContentContainer>
                </Node>
            )}

            {!hideFooter && <GuestPageFooter isEditing={isEditing} isPreview={shouldPreview} showCalendar />}
            <Node
                is={SpeakerModal}
                displayName={NODE_DISPLAY_NAME_SPEAKER_MODALS}
                id={NODE_ID_SPEAKER_MODALS}
                {...agendaPageConfig.speakerModals}
            />
            <Node
                is={AddToCalendarModal}
                displayName={NODE_DISPLAY_NAME_ADD_SESSION_TO_CALENDAR_MODAL}
                id={NODE_ID_ADD_SESSION_TO_CALENDAR}
                {...agendaPageConfig[NODE_ID_ADD_SESSION_TO_CALENDAR]}
            />
            <SuiNotificationGroup notifications={notificationDismissed ? [] : activeNotifications} />
        </AgendaGuestExperienceContainer>
    );
};

AgendaContent.propTypes = {
    hideFooter: PropTypes.bool,
};

AgendaContent.defaultProps = {
    hideFooter: false,
};

export default AgendaContent;
